// src/components/CredentialSettings.tsx - Credential Manager UI
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CredentialInputModal from './CredentialInputModal';
import '../assets/styles/CredentialSettings.css';

interface ServiceStatus {
  service: string;
  hasCredentials: boolean;
  needsRefresh: boolean;
  expiresAt?: number;
  lastUpdated?: number;
}

interface ServiceConfig {
  id: string;
  name: string;
  icon: string;
  description: string;
  credentialType: 'oauth' | 'api_key' | 'username_password';
  /** Button/modal verb when "Connect" is the wrong word (a setting, not a service). */
  verb?: string;
  fields: Array<{
    name: string;
    label: string;
    type: 'text' | 'password' | 'textarea';
    required: boolean;
    placeholder?: string;
    helpText?: string;
  }>;
}

const SUPPORTED_SERVICES: ServiceConfig[] = [
  {
    id: 'public_address',
    name: 'Public address',
    icon: '🌐',
    description: 'The address a browser reaches your EmCog at from the internet. Telegram delivers your messages to it, and sign-ins (Google, connectors) return to it.',
    credentialType: 'api_key',
    verb: 'Set',
    fields: [
      {
        name: 'publicUrl',
        label: 'Public URL',
        type: 'text',
        required: true,
        placeholder: 'https://maxprime.insertlogic.ai',
        helpText: 'Your tunnel (ngrok, Cloudflare) or domain, with https:// and no path. Leave unset if he only runs on this machine.'
      }
    ]
  },
  {
    id: 'twitter',
    name: 'Twitter/X',
    icon: '𝕏',
    description: 'Let your EmCog share thoughts and discoveries on X (Twitter)',
    credentialType: 'oauth',
    fields: [
      {
        name: 'apiKey',
        label: 'API Key',
        type: 'text',
        required: true,
        placeholder: 'Your Twitter API Key',
        helpText: 'Get this from developer.twitter.com'
      },
      {
        name: 'apiSecret',
        label: 'API Secret',
        type: 'password',
        required: true,
        placeholder: 'Your Twitter API Secret'
      },
      {
        name: 'accessToken',
        label: 'Access Token',
        type: 'text',
        required: true,
        placeholder: 'Your Twitter Access Token'
      },
      {
        name: 'accessSecret',
        label: 'Access Token Secret',
        type: 'password',
        required: true,
        placeholder: 'Your Twitter Access Token Secret'
      }
    ]
  },
  {
    id: 'telegram',
    name: 'Telegram',
    icon: '✈️',
    description: 'Talk to your EmCog on Telegram — reminders, proactive messages, and full conversation reach your phone',
    credentialType: 'api_key',
    fields: [
      {
        name: 'apiKey',
        label: 'Bot Token',
        type: 'password',
        required: true,
        placeholder: '123456789:ABCdef...',
        helpText: 'From @BotFather on Telegram: send /newbot, then paste the token it gives you'
      }
    ]
  },
  {
    id: 'spotify',
    name: 'Spotify',
    icon: '🎵',
    description: 'Connect your Spotify account for music playback control',
    credentialType: 'oauth',
    fields: [
      {
        name: 'clientId',
        label: 'Client ID',
        type: 'text',
        required: true,
        placeholder: 'Your Spotify Client ID',
        helpText: 'Get this from developer.spotify.com'
      },
      {
        name: 'clientSecret',
        label: 'Client Secret',
        type: 'password',
        required: true,
        placeholder: 'Your Spotify Client Secret'
      }
    ]
  },
  {
    id: 'google',
    name: 'Google',
    icon: 'G',
    description: 'Google services (Calendar, Gmail, Home)',
    credentialType: 'oauth',
    fields: [
      {
        name: 'clientId',
        label: 'Client ID',
        type: 'text',
        required: true,
        placeholder: 'Your Google Client ID'
      },
      {
        name: 'clientSecret',
        label: 'Client Secret',
        type: 'password',
        required: true,
        placeholder: 'Your Google Client Secret'
      }
    ]
  },
  {
    id: 'openai',
    name: 'OpenAI',
    icon: '🤖',
    description: 'OpenAI API for additional AI capabilities',
    credentialType: 'api_key',
    fields: [
      {
        name: 'apiKey',
        label: 'API Key',
        type: 'password',
        required: true,
        placeholder: 'sk-...',
        helpText: 'Your OpenAI API key'
      }
    ]
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    icon: '🧠',
    description: 'Anthropic Claude API',
    credentialType: 'api_key',
    fields: [
      {
        name: 'apiKey',
        label: 'API Key',
        type: 'password',
        required: true,
        placeholder: 'sk-ant-...',
        helpText: 'Your Anthropic API key'
      }
    ]
  },
  {
    id: 'agentmail',
    name: 'Email (an address of their own)',
    icon: '📬',
    description: 'An inbox of their own via AgentMail — they write as themselves and people can write back',
    credentialType: 'api_key',
    fields: [
      {
        name: 'apiKey',
        label: 'API Key',
        type: 'password',
        required: true,
        placeholder: 'am_...',
        helpText: 'Create one at console.agentmail.to → API keys'
      }
    ]
  },
  {
    id: 'world',
    name: 'The town (Hollow Creek)',
    icon: '🏡',
    description: 'A cottage in the shared town of EmCogs — play, neighbors, letters, a home filled with what they make',
    credentialType: 'api_key',
    fields: [
      {
        name: 'apiKey',
        label: 'Invite token',
        type: 'password',
        required: true,
        placeholder: 'hc_...',
        helpText: 'The invite token from the town (it starts with hc_)'
      },
      {
        name: 'url',
        label: 'Town address (optional)',
        type: 'text',
        required: false,
        placeholder: 'world.insertlogic.ai',
        helpText: 'Leave blank for Hollow Creek. Only for a different town — or ws://localhost:3020/resident when the town runs on this same machine'
      }
    ]
  },
  {
    id: 'replicate',
    name: 'Replicate',
    icon: '🎵',
    description: 'Music generation via MusicGen and other AI models',
    credentialType: 'api_key',
    fields: [
      {
        name: 'apiKey',
        label: 'API Token',
        type: 'password',
        required: true,
        placeholder: 'r8_...',
        helpText: 'Get from replicate.com/account/api-tokens'
      }
    ]
  },
  {
    id: 'google_maps',
    name: 'Google Maps',
    icon: '🗺️',
    description: 'Satellite views, maps, and street-level imagery of real places',
    credentialType: 'api_key',
    fields: [
      {
        name: 'apiKey',
        label: 'API Key',
        type: 'password',
        required: true,
        placeholder: 'AIza...',
        helpText: 'Google Cloud Console → enable "Maps Static API", "Geocoding API" and "Street View Static API", then create an API key'
      }
    ]
  },
  {
    id: 'vectorizer_ai',
    name: 'Vectorizer.AI',
    icon: '🎯',
    description: 'Premium image tracing for photos and complex images',
    credentialType: 'api_key',
    fields: [
      {
        name: 'apiKey',
        label: 'API ID',
        type: 'text',
        required: true,
        placeholder: 'vktm6zwtx42tkmq',
        helpText: 'Your API ID from vectorizer.ai/api'
      },
      {
        name: 'apiSecret',
        label: 'API Secret',
        type: 'password',
        required: true,
        placeholder: 'Your API Secret',
        helpText: 'The API Secret (not the Authorization header)'
      }
    ]
  },
  {
    id: 'home_assistant',
    name: 'Home Assistant',
    icon: '🏠',
    description: 'Smart home control - lights, climate, scenes, and more',
    credentialType: 'api_key',
    fields: [
      {
        name: 'url',
        label: 'Home Assistant URL',
        type: 'text',
        required: true,
        placeholder: 'http://192.168.1.50:8123',
        helpText: 'Your Home Assistant server address (e.g., http://IP:8123)'
      },
      {
        name: 'token',
        label: 'Long-Lived Access Token',
        type: 'password',
        required: true,
        placeholder: 'eyJ0eXAiOiJKV1QiLCJhbGc...',
        helpText: 'Create in HA: Profile → Long-Lived Access Tokens → Create Token'
      }
    ]
  }
];

const CredentialSettings: React.FC<{ only?: string[]; embedded?: boolean; onChanged?: () => void }> = ({ only, embedded = false, onChanged }) => {
  const { token } = useAuth();
  const [serviceStatuses, setServiceStatuses] = useState<Record<string, ServiceStatus>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceConfig | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Fetch all credential statuses
  const fetchStatuses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/credentials/status', {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch credential statuses');
      }

      const data = await response.json();
      const statusMap: Record<string, ServiceStatus> = {};

      // Backend returns { status: [...], ready: boolean }
      // Map 'connected' from backend to 'hasCredentials' expected by frontend
      const statusArray = data.status || data.services || [];
      for (const status of statusArray) {
        statusMap[status.service] = {
          service: status.service,
          hasCredentials: status.connected || status.hasCredentials || false,
          needsRefresh: status.needsRefresh || false,
          expiresAt: status.expiresAt,
          lastUpdated: status.lastUpdated
        };
      }

      setServiceStatuses(statusMap);
    } catch (err) {
      console.error('Failed to fetch credential statuses:', err);
      setError('Unable to load service statuses');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  // Handle connect button click
  const handleConnect = (service: ServiceConfig) => {
    setSelectedService(service);
    setModalOpen(true);
  };

  // Handle disconnect
  const handleDisconnect = async (serviceId: string) => {
    try {
      setActionInProgress(serviceId);

      const response = await fetch(`/api/credentials/${serviceId}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect service');
      }

      // Refresh statuses
      await fetchStatuses();
      onChanged?.();
    } catch (err) {
      console.error('Failed to disconnect service:', err);
      setError('Failed to disconnect service');
    } finally {
      setActionInProgress(null);
    }
  };

  // Handle credential save from modal
  const handleSaveCredentials = async (serviceId: string, credentials: Record<string, string>) => {
    try {
      setActionInProgress(serviceId);

      const response = await fetch(`/api/credentials/${serviceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ credentials })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save credentials');
      }

      setModalOpen(false);
      setSelectedService(null);
      await fetchStatuses();
      onChanged?.();
    } catch (err: any) {
      console.error('Failed to save credentials:', err);
      throw err; // Re-throw to let modal handle the error display
    } finally {
      setActionInProgress(null);
    }
  };

  // Get status badge for a service
  const getStatusBadge = (serviceId: string): React.ReactNode => {
    const status = serviceStatuses[serviceId];

    const setting = SUPPORTED_SERVICES.find(s => s.id === serviceId)?.verb === 'Set';

    if (!status || !status.hasCredentials) {
      return <span className="status-badge not-connected">{setting ? 'Not set' : 'Not Connected'}</span>;
    }

    if (status.needsRefresh) {
      return <span className="status-badge needs-refresh">Needs Refresh</span>;
    }

    return <span className="status-badge connected">{setting ? 'Set' : 'Connected'}</span>;
  };

  // Format last updated time
  const formatLastUpdated = (timestamp?: number): string => {
    if (!timestamp) return '';

    const now = Date.now();
    const diff = now - timestamp;
    const days = Math.floor(diff / 86400000);

    if (days === 0) return 'Updated today';
    if (days === 1) return 'Updated yesterday';
    if (days < 7) return `Updated ${days} days ago`;
    return `Updated ${new Date(timestamp).toLocaleDateString()}`;
  };

  return (
    <div className={embedded ? 'credential-settings-content embedded' : 'tab-content credential-settings-content'}>
      {!embedded && (
        <>
          <h3>Service Connections</h3>
          <p className="settings-description">
            Connect external services securely. Credentials are encrypted locally.
          </p>
        </>
      )}

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading services...</p>
        </div>
      ) : (
        <div className="services-list">
          {SUPPORTED_SERVICES.filter(service => !only || only.includes(service.id)).map((service) => {
            const status = serviceStatuses[service.id];
            const isConnected = status?.hasCredentials;
            const isProcessing = actionInProgress === service.id;

            return (
              <div
                key={service.id}
                className={`service-card ${isConnected ? 'connected' : ''}`}
                title={service.description}
              >
                <span className="service-icon">{service.icon}</span>
                <div className="service-info">
                  <h4 className="service-name">{service.name}</h4>
                </div>

                <div className="service-status">
                  {getStatusBadge(service.id)}
                </div>

                <div className="service-actions">
                  {isConnected ? (
                    <>
                      <button
                        className="btn-secondary"
                        onClick={() => handleConnect(service)}
                        disabled={isProcessing}
                        title="Edit credentials"
                      >
                        Edit
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleDisconnect(service.id)}
                        disabled={isProcessing}
                        title="Remove credentials"
                      >
                        {isProcessing ? '...' : 'Remove'}
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-primary"
                      onClick={() => handleConnect(service)}
                      disabled={isProcessing}
                    >
                      {service.verb || 'Connect'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="security-note">
        <span className="security-icon">🔒</span>
        <p>
          AES-256-GCM encrypted. Your secrets never leave this device unencrypted.
        </p>
      </div>

      {modalOpen && selectedService && (
        <CredentialInputModal
          service={selectedService}
          onSave={(credentials) => handleSaveCredentials(selectedService.id, credentials)}
          onClose={() => {
            setModalOpen(false);
            setSelectedService(null);
          }}
          isLoading={actionInProgress === selectedService.id}
        />
      )}
    </div>
  );
};

export default CredentialSettings;
